import { VisualController } from '@sas-dvr/ltjs-bird';
import { withRetained } from '../util';
import { BIRDModelControllerLoader } from './LoaderFactory';
import { WarningEvent, Warning } from '@sas-dvr/ltjs-commons';
import { of, merge } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { fromPortableEvent } from '../util';
import { WarningState } from '@sas-dvr/internal-va-react-core/store/warning/WarningState';

export const loadVisualController: BIRDModelControllerLoader = (
  controller,
  unload$,
  myActions$,
  state$,
  deps
) => {
  if (!(controller instanceof VisualController)) {
    throw new Error('Expected the controller to be a VisualController');
  }

  const visualElementController = controller.getVisualElementController();
  if (!visualElementController) {
    throw new Error('Expected the VisualController to have a VisualElementController');
  }

  const id = deps.getKey(controller);

  //
  // Handle warnings on the visual
  //
  const handleWarnings$ = fromPortableEvent<WarningEvent>(controller, WarningEvent.GENERIC).pipe(
    mergeMap((event) => {
      const allWarnings = event.getWarnings();
      if (!allWarnings) {
        return of(WarningState.actions.set(id, undefined));
      }
      //
      // Filter out all warnings with severity < 40.0.
      // Defect S1327500.
      //
      const allFilteredWarnings = allWarnings.filter(function (warning) {
        return warning && warning.getSeverity() >= 40;
      }) as Warning[];

      if (!allFilteredWarnings.length) {
        return of(WarningState.actions.set(id, undefined));
      }
      // allWarnings is a sorted array returned by portable in descending order of the severity.
      const highestSeverity = allFilteredWarnings[0].getSeverity();
      if (highestSeverity >= Warning.SEVERITY_FATAL_MIN) {
        // If there is a fatal severity warning, just show the top most one as an error pill.
        // Do not combine all other messages.
        // TODO: should we default to a generic error message rather than ''? or is this not a realistic use case?
        return of(
          WarningState.actions.set(id, {
            type: 'error',
            message: allFilteredWarnings[0].getMessage() || '',
          })
        );
      } else if (highestSeverity >= Warning.SEVERITY_ERROR_MIN) {
        // If there is a error severity warning, just show the top most one as an information pill.
        // Do not combine all other messages.
        return of(
          WarningState.actions.set(id, {
            type: 'information',
            message: allFilteredWarnings[0].getMessage() || '',
          })
        );
      } else {
        // All others are combined and shown as a warning badge
        let allWarningMessages = '';
        for (const warning of allFilteredWarnings) {
          allWarningMessages += warning.getMessage();
        }
        return of(WarningState.actions.set(id, { type: 'warning', message: allWarningMessages }));
      }
    })
  );

  const loader = deps.getLoaderForVisualElementController(visualElementController);

  return merge(
    handleWarnings$,
    loader(controller, visualElementController, unload$, myActions$, state$, deps).pipe(
      withRetained(visualElementController)
    )
  );
};
